import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useCallback, useRef } from 'react';
import Cookies from 'js-cookie';
import { useAuthStore } from '../stores';
import { API_BASE_URL } from '../config/api-config';

/**
 * Passport SSO Hook
 *
 * Handles authentication via Omniverse Passport for apps on the .tracverse.com domain.
 * Since all apps share the same root domain, cookies are automatically shared.
 *
 * Flow:
 * 1. User logs in at auth.tracverse.com
 * 2. Cookies (omni_session, omni_refresh) are set on .tracverse.com domain
 * 3. Any app on *.tracverse.com can read these cookies
 * 4. This hook verifies the session and populates the auth store
 */
export function usePassportSSO() {
  const { login, logout, setLoading, user, isAuthenticated } = useAuthStore();
  const queryClient = useQueryClient();
  const hasCheckedRef = useRef(false);

  // Note: omni_session and omni_refresh are HttpOnly cookies, so they're NOT visible
  // to JavaScript via document.cookie. The browser sends them automatically with
  // credentials: 'include' requests. We let the verify request determine auth state.

  // Helper to refresh access token using refresh token cookie
  const refreshAccessToken = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        console.log('[PassportSSO] Token refresh failed - session may be expired');
        return false;
      }

      console.log('[PassportSSO] Access token refreshed');
      return true;
    } catch (err) {
      console.error('[PassportSSO] Token refresh error:', err.message);
      return false;
    }
  }, []);

  // Check for Passport session via cookie-based auth
  const { data: sessionData, isLoading, error, refetch, isFetched } = useQuery({
    queryKey: ['passport', 'verify'],
    queryFn: async () => {
      // Note: We always make the verify request because omni_session/omni_refresh are
      // HttpOnly cookies that aren't visible to JavaScript. The browser sends them
      // automatically with credentials: 'include', and the server tells us if valid.
      try {
        const response = await fetch(`${API_BASE_URL}/api/auth/verify`, {
          method: 'GET',
          credentials: 'include', // Send cookies
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));

          // Check if session was revoked
          if (errorData.code === 'SESSION_REVOKED' || errorData.code === 'TOKEN_REVOKED') {
            console.log('[PassportSSO] Session was revoked remotely');
            return { valid: false, user: null, revoked: true };
          }

          // Token expired - try to refresh it
          if (errorData.code === 'TOKEN_EXPIRED' || errorData.error?.includes('expired')) {
            console.log('[PassportSSO] Access token expired, attempting refresh...');
            const refreshed = await refreshAccessToken();

            if (refreshed) {
              // Retry verify after refresh
              const retryResponse = await fetch(`${API_BASE_URL}/api/auth/verify`, {
                method: 'GET',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
              });

              if (retryResponse.ok) {
                const data = await retryResponse.json();
                if (data.success && data.user) {
                  return {
                    valid: true,
                    user: data.user,
                    walletAddress: data.walletAddress || data.user.walletAddress || data.user.creator,
                  };
                }
              }
            }

            // Refresh failed - session is truly expired
            return { valid: false, user: null };
          }

          // No valid session - this is expected for first-time visitors
          return { valid: false, user: null };
        }

        const data = await response.json();

        if (data.success && data.user) {
          // Include walletAddress from the verify response
          return {
            valid: true,
            user: data.user,
            walletAddress: data.walletAddress || data.user.walletAddress || data.user.creator,
          };
        }

        return { valid: false, user: null, walletAddress: null };
      } catch (err) {
        console.debug('[PassportSSO] Session check failed:', err.message);
        return { valid: false, user: null };
      }
    },
    staleTime: 0, // Always consider data stale - we check cookies in queryFn anyway
    gcTime: 5 * 60 * 1000, // 5 minutes cache for garbage collection
    refetchOnWindowFocus: true, // Re-check when user returns to tab
    refetchOnMount: 'always', // ALWAYS refetch on mount to validate cookies
    refetchInterval: isAuthenticated ? 10 * 60 * 1000 : false, // Poll every 10 minutes if logged in
    retry: false, // Don't retry on 401 - we handle refresh manually
  });

  // Update auth store when session data changes
  useEffect(() => {
    // Still loading - don't do anything yet
    if (isLoading) return;

    // sessionData is undefined until first fetch completes
    if (sessionData === undefined) return;

    // Handle revoked session - auto-logout and redirect
    if (sessionData?.revoked && isAuthenticated) {
      console.log('[PassportSSO] Session revoked - logging out');
      Cookies.remove('omni_acc', { path: '/' });
      Cookies.remove('omni_session', { path: '/' });
      Cookies.remove('omni_refresh', { path: '/' });
      logout();
      queryClient.clear();

      // Redirect to passport login with message
      const passportUrl = import.meta.env.VITE_PASSPORT_URL || 'https://auth.tracverse.com';
      window.location.href = `${passportUrl}/login?reason=session_revoked&redirect=${encodeURIComponent(window.location.origin)}`;
      return;
    }

    // Session check complete - user has valid session
    if (sessionData?.valid && sessionData.user) {
      // User has valid Passport session - log them in
      const passportUser = sessionData.user;

      // Get wallet address from verify response (unmasked)
      const walletAddress =
        sessionData.walletAddress ||
        passportUser.walletAddress ||
        passportUser.creator ||
        passportUser.linkedProviders?.wallet?.address;

      // Set omni_acc cookie with wallet address (this one is NOT HttpOnly)
      const existingOmniAcc = Cookies.get('omni_acc');
      if (walletAddress && existingOmniAcc !== walletAddress) {
        Cookies.set('omni_acc', walletAddress, { path: '/', secure: true, sameSite: 'none' });
        console.log('[PassportSSO] Set omni_acc cookie');
      }

      // Map Passport user to TalkTrail user format
      const talktrailUser = {
        _id: passportUser._id,
        passportId: passportUser.passportId,
        username: passportUser.username,
        oid: passportUser.oid,
        avatar: passportUser.avatar || passportUser.thumbnail,
        thumbnail: passportUser.thumbnail,
        bio: passportUser.bio,
        role: passportUser.role || 'user',
        isVerified: passportUser.isVerified,
        // Use wallet address from verify response
        creator: walletAddress,
        wallet: walletAddress,
        // Platform access
        platformAccess: passportUser.platformAccess,
        // Stats
        followers: passportUser.followers || 0,
        following: passportUser.following || 0,
        // Auth metadata
        primaryAuthMethod: passportUser.primaryAuthMethod,
        linkedProviders: passportUser.linkedProviders,
        // Timestamps
        createdAt: passportUser.createdAt,
        lastLogin: passportUser.lastLogin,
      };

      // Only update if not already logged in with same user
      if (!isAuthenticated || user?.passportId !== talktrailUser.passportId) {
        console.log('[PassportSSO] Auto-login via Passport session:', talktrailUser.passportId);
        login(talktrailUser);
      }

      hasCheckedRef.current = true;
    } else if (sessionData?.valid === false && !hasCheckedRef.current) {
      // Only logout on initial check if there's no valid session
      // This prevents logout during network hiccups on subsequent checks
      console.log('[PassportSSO] No valid session on initial check');
      Cookies.remove('omni_acc', { path: '/' });
      // Clear any stale auth state
      if (isAuthenticated) {
        logout();
      }
      hasCheckedRef.current = true;
    }
  }, [sessionData, isLoading, login, logout, isAuthenticated, user?.passportId, queryClient]);

  // Clear loading state when query completes (whether successful or not)
  useEffect(() => {
    if (isFetched && !isLoading) {
      // Only clear loading if we've actually completed the check
      setLoading(false);
    }
  }, [isFetched, isLoading, setLoading]);

  // Redirect to Passport login
  const redirectToLogin = useCallback(() => {
    const currentUrl = window.location.href;
    const passportUrl = import.meta.env.VITE_PASSPORT_URL || 'https://auth.tracverse.com';
    window.location.href = `${passportUrl}/login?redirect=${encodeURIComponent(currentUrl)}&platform=talktrail`;
  }, []);

  // Logout via Passport
  const passportLogout = useCallback(async () => {
    try {
      await fetch(`${API_BASE_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (err) {
      console.error('[PassportSSO] Logout failed:', err);
    }

    // Clear omni_acc cookie
    Cookies.remove('omni_acc', { path: '/' });

    // Clear local state
    logout();
    queryClient.clear();

    // Redirect to Passport logout
    const passportUrl = import.meta.env.VITE_PASSPORT_URL || 'https://auth.tracverse.com';
    window.location.href = `${passportUrl}/logout?redirect=${encodeURIComponent(window.location.origin)}`;
  }, [logout, queryClient]);

  return {
    isCheckingSession: isLoading,
    hasPassportSession: sessionData?.valid ?? false,
    passportUser: sessionData?.user ?? null,
    error,
    refetch,
    redirectToLogin,
    passportLogout,
  };
}

/**
 * Check if user has the omni_acc cookie (indicates a previous login)
 * Note: omni_session is HttpOnly so we can't check it from JS.
 * This is just a hint - always verify with the server.
 */
export function hasPassportCookie() {
  return document.cookie.includes('omni_acc=');
}

export default usePassportSSO;
